// Right-stack cards: horse context + order context.
// Mirrors aside.right-stack in the approved HTML.
//
// W26 (2026-10-20): two modes.
//   - preview (default, no props): the approved design fixture (Duke,
//     order EX-1042). Only reachable in ?preview= / dev-mock sessions.
//   - real: horses from GET /v1/contacts/:id/horses rendered as a list
//     (or an honest empty / error state).
//
// W29 (2026-10-20): in real mode the "Latest order" card reads
// GET /api/customers/:id/history?scope=orders (R6 history route, backed
// on the api-server by sinks/shopify-orders-sink) and renders the
// contact's latest Shopify order: order name, date, total, fulfillment
// and payment status, up to three line items, and "View all orders" to
// /history/:customerId. States:
//   - 503 CUSTOMER_HISTORY_NOT_CONFIGURED or SHOPIFY_ORDERS_NOT_CONFIGURED
//     keeps the pre-W29 placeholder (the order sync is not linked on
//     that deploy).
//   - 200 with an `order` entry: the real order.
//   - 200 with only an `order_summary` entry plus source note
//     shopify_60_day_window: the CRM Admin app lacks read_all_orders
//     (MEASURED 2026-10-20), so the card renders the HubSpot summary
//     (last order name / date / count / total spent) with the note
//     "Order detail older than 60 days needs Shopify all-orders access".
//     It never says "no orders" for a customer who has them.
//   - 200 with no entries: "No Shopify orders on file".
//   - any other failure: "Latest order could not load (CODE)".
// The customer id comes from the route (/current/:customerId) unless the
// parent passes one; the session headers are the same ones the profile
// and collection hooks send (useContactProfile.buildSessionHeaders).
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../_shared/IconSprite.js';
import type { CollectionResult, HorseRecord } from '../../_shared/useContactCollections.js';
import { buildSessionHeaders, useContactReadGate } from '../../_shared/useContactProfile.js';
import { CURRENT_ACTION_MOCK } from '../mockData.js';
import { DUKE, HORSE_PORTRAIT_PLACEHOLDER } from '../../../data/mockData.js';

export type RightStackProps =
  | { mode?: 'preview' }
  | {
      mode: 'real';
      horses: CollectionResult<HorseRecord>;
      onViewHorses?: () => void;
      /** Defaults to the :customerId route param. */
      customerId?: string;
    };

export function horseSubtitle(h: HorseRecord): string {
  return [h.breed, h.age ? `${h.age} yrs` : null, h.gender, h.discipline]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' · ');
}

// ---------------------------------------------------------------------
// Latest order: types, pure helpers, hook. Exported for unit tests.
// ---------------------------------------------------------------------

export const ORDER_SYNC_PLACEHOLDER = 'Orders will appear here once the Shopify order sync is linked.';
export const NO_ORDERS_TEXT = 'No Shopify orders on file';
export const SIXTY_DAY_WINDOW_NOTE = 'Order detail older than 60 days needs Shopify all-orders access';
export const SIXTY_DAY_WINDOW_REASON = 'shopify_60_day_window';
/** 503 codes that mean "not wired on this deploy" rather than "failed". */
export const NOT_CONFIGURED_CODES: ReadonlySet<string> = new Set([
  'CUSTOMER_HISTORY_NOT_CONFIGURED',
  'SHOPIFY_ORDERS_NOT_CONFIGURED',
]);

export function latestOrderPath(customerId: string): string {
  return `/api/customers/${encodeURIComponent(customerId)}/history?scope=orders&limit=10`;
}

export interface OrderLineItem {
  title: string | null;
  qty: number | null;
  sku: string | null;
}

export interface OrderEntry {
  id: string | null;
  ts: string | null;
  orderName: string | null;
  orderId: string | null;
  total: string | null;
  currency: string | null;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  lineItems: OrderLineItem[];
}

export interface OrderSummaryEntry {
  id: string | null;
  ts: string | null;
  ordersCount: number | null;
  lastOrderName: string | null;
  lastOrderDate: string | null;
  totalSpent: string | null;
  note: string | null;
}

export interface LatestOrderView {
  latest: OrderEntry | null;
  summary: OrderSummaryEntry | null;
  sixtyDayWindow: boolean;
  total: number;
}

export type LatestOrderStatus = 'skipped' | 'loading' | 'ready' | 'not-configured' | 'error';

export interface LatestOrderState {
  status: LatestOrderStatus;
  view: LatestOrderView | null;
  errorCode?: string;
}

type Dict = Record<string, unknown>;

function isDict(value: unknown): value is Dict {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function count(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.trunc(value));
  if (typeof value === 'string' && /^\d+(?:\.0+)?$/.test(value.trim())) return Number.parseInt(value, 10);
  return null;
}

function orderEntry(raw: Dict): OrderEntry {
  const details = isDict(raw.details) ? raw.details : {};
  const lines = Array.isArray(details.lineItems) ? details.lineItems : [];
  return {
    id: str(raw.id),
    ts: str(raw.ts),
    orderName: str(details.orderName),
    orderId: str(details.orderId),
    total: str(details.total),
    currency: str(details.currency),
    financialStatus: str(details.financialStatus),
    fulfillmentStatus: str(details.fulfillmentStatus),
    lineItems: lines.filter(isDict).map((li) => ({
      title: str(li.title),
      qty: count(li.qty),
      sku: str(li.sku),
    })),
  };
}

function summaryEntry(raw: Dict): OrderSummaryEntry {
  const details = isDict(raw.details) ? raw.details : {};
  return {
    id: str(raw.id),
    ts: str(raw.ts),
    ordersCount: count(details.ordersCount),
    lastOrderName: str(details.lastOrderName),
    lastOrderDate: str(details.lastOrderDate),
    totalSpent: str(details.totalSpent),
    note: str(details.note),
  };
}

function noteReason(note: unknown): string | null {
  if (typeof note === 'string') return note;
  if (isDict(note)) return str(note.reason);
  return null;
}

function newestFirst(a: { ts: string | null }, b: { ts: string | null }): number {
  const at = a.ts ?? '';
  const bt = b.ts ?? '';
  if (at === bt) return 0;
  if (!at) return 1;
  if (!bt) return -1;
  return bt.localeCompare(at);
}

/**
 * Reduce a 200 body from /api/customers/:id/history?scope=orders to what
 * the card renders. Accepts source notes as strings or {reason} objects
 * (the sink emits strings; the history-adapter wraps them).
 */
export function deriveLatestOrderView(body: unknown): LatestOrderView {
  const entries = isDict(body) && Array.isArray(body.entries) ? body.entries.filter(isDict) : [];
  const notes = isDict(body) && Array.isArray(body.sourceNotes) ? body.sourceNotes : [];
  const orders = entries.filter((e) => e.kind === 'order').map(orderEntry).sort(newestFirst);
  const summaries = entries.filter((e) => e.kind === 'order_summary').map(summaryEntry).sort(newestFirst);
  return {
    latest: orders[0] ?? null,
    summary: summaries[0] ?? null,
    sixtyDayWindow: notes.some((n) => noteReason(n) === SIXTY_DAY_WINDOW_REASON),
    total: entries.length,
  };
}

export function formatMoney(amount: string | number | null | undefined, currency: string | null | undefined): string | null {
  const n = typeof amount === 'number' ? amount : Number.parseFloat(amount ?? '');
  if (!Number.isFinite(n)) return null;
  const code = currency && /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(n);
  } catch {
    return `${n.toFixed(2)} ${code}`;
  }
}

/**
 * "Oct 2, 2026". Date-only values (HubSpot date props, "YYYY-MM-DD") are
 * rendered as that calendar day; timestamps are rendered in Central time
 * (the reps and the shop are in Dallas) so an evening order keeps its day.
 */
export function formatOrderDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly) {
    const local = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return local.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Chicago',
  });
}

/** "PARTIALLY_FULFILLED" -> "Partially fulfilled". */
export function humanizeStatus(value: string | null | undefined): string | null {
  if (!value) return null;
  const words = value.trim().toLowerCase().replace(/_+/g, ' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
}

export function useLatestOrder(customerId: string | null | undefined): LatestOrderState {
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;
  const [state, setState] = useState<LatestOrderState>(() => ({
    status: canFetch ? 'loading' : 'skipped',
    view: null,
  }));
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!canFetch || !customerId || !gate.session) {
      setState({ status: 'skipped', view: null });
      return;
    }
    const requestId = `${customerId}:orders:${Date.now()}`;
    latest.current = requestId;
    setState({ status: 'loading', view: null });
    const headers = buildSessionHeaders(gate.session);
    fetch(latestOrderPath(customerId), { method: 'GET', headers })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (!response.ok) {
          let errorCode = `HTTP_${response.status}`;
          try {
            const parsed = JSON.parse(await response.text()) as { errorCode?: string };
            if (parsed && typeof parsed.errorCode === 'string') errorCode = parsed.errorCode;
          } catch {
            /* non-JSON or empty error body */
          }
          const notConfigured = response.status === 503 && NOT_CONFIGURED_CODES.has(errorCode);
          setState({ status: notConfigured ? 'not-configured' : 'error', view: null, errorCode });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          setState({ status: 'error', view: null, errorCode: 'BAD_JSON' });
          return;
        }
        setState({ status: 'ready', view: deriveLatestOrderView(body) });
      })
      .catch(() => {
        if (latest.current !== requestId) return;
        setState({ status: 'error', view: null, errorCode: 'FETCH_FAILED' });
      });
    return () => {
      if (latest.current === requestId) latest.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, sessionIdentity, sessionToken]);

  return state;
}

// ---------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------

function RealHorseCard({ horses, onViewHorses }: { horses: CollectionResult<HorseRecord>; onViewHorses?: () => void }): ReactNode {
  const named = horses.items.filter((h) => typeof h.name === 'string' && h.name.trim().length > 0);
  return (
    <section className="card" data-testid="horse-card">
      <div className="context" aria-busy={horses.status === 'loading'}>
        <span className="eyebrow">Every horse has a story</span>
        {horses.status === 'loading' ? (
          <p className="muted" role="status">Loading horses…</p>
        ) : horses.status === 'error' ? (
          <p className="muted" role="alert">Horses could not load ({horses.errorCode || 'ERR'}).</p>
        ) : named.length === 0 ? (
          <p className="muted" data-testid="horse-card-empty">
            No horse on file yet. Ask during the call and add it.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
            {named.map((h, idx) => {
              const subtitle = horseSubtitle(h);
              return (
                <li key={h.id ?? `horse-${idx}`} className="row" data-testid="horse-card-item">
                  <span className="horse-monogram" aria-hidden="true">
                    {(h.name as string).trim().charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <h3>{(h.name as string).trim()}</h3>
                    <p className="muted">{subtitle || 'Details not on file yet'}</p>
                    {h.products || h.productsFeeding ? (
                      <p className="muted">Feeding: {h.productsFeeding || h.products}</p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <button className="card-link" type="button" onClick={onViewHorses}>
        {named.length === 0 ? 'Add a horse' : 'View horse & feeding details'}
        <Icon id="arrow" />
      </button>
    </section>
  );
}

function orderTitle(order: OrderEntry): string {
  const first = order.lineItems.find((li) => li.title);
  return first?.title ?? order.orderName ?? 'Order';
}

function lineLabel(li: OrderLineItem): string {
  const name = li.title ?? li.sku ?? 'Item';
  const qty = li.qty && li.qty > 1 ? ` × ${li.qty}` : '';
  const sku = li.sku && li.title ? ` (${li.sku})` : '';
  return `${name}${qty}${sku}`;
}

function LatestOrderBody({ order }: { order: LatestOrderState }): ReactNode {
  const view = order.status === 'ready' ? order.view : null;
  const latest = view?.latest ?? null;
  const summary = view?.summary ?? null;

  if (order.status === 'loading') {
    return <p className="muted" role="status">Loading latest order…</p>;
  }
  if (order.status === 'error') {
    return (
      <p className="muted" role="alert" data-testid="order-card-error">
        Latest order could not load ({order.errorCode || 'ERR'}).
      </p>
    );
  }
  if (latest) {
    const date = formatOrderDate(latest.ts);
    const total = formatMoney(latest.total, latest.currency);
    const fulfillment = humanizeStatus(latest.fulfillmentStatus);
    const payment = humanizeStatus(latest.financialStatus);
    const lines = latest.lineItems.slice(0, 3);
    return (
      <>
        <div className="row" style={{ marginBottom: 0 }}>
          <div className="product-mark">
            <Icon id="box" />
          </div>
          <div>
            <h3 data-testid="order-card-title">{orderTitle(latest)}</h3>
            <p className="muted" data-testid="order-card-meta">
              Order {latest.orderName ?? 'number not on file'}
              {date ? ` · ${date}` : ''}
            </p>
          </div>
          {fulfillment ? (
            <span className="badge" style={{ marginLeft: 'auto' }} data-testid="order-card-fulfillment">
              {fulfillment}
            </span>
          ) : null}
        </div>
        <div className="order-info">
          <span className="muted" data-testid="order-card-total">{total ?? 'Total not on file'}</span>
          {payment ? <span style={{ color: '#37735c' }} data-testid="order-card-payment">{payment}</span> : null}
        </div>
        {lines.length > 0 ? (
          <ul data-testid="order-card-lines" style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: 4 }}>
            {lines.map((li, idx) => (
              <li key={`${li.sku ?? li.title ?? 'item'}-${idx}`} className="muted">{lineLabel(li)}</li>
            ))}
            {latest.lineItems.length > lines.length ? (
              <li className="muted">+{latest.lineItems.length - lines.length} more</li>
            ) : null}
          </ul>
        ) : null}
        {summary && view?.sixtyDayWindow ? (
          <p className="muted" data-testid="order-card-window-note">
            {summary.ordersCount !== null ? `${summary.ordersCount} orders on file. ` : ''}
            {SIXTY_DAY_WINDOW_NOTE}
          </p>
        ) : null}
      </>
    );
  }
  if (summary) {
    const date = formatOrderDate(summary.lastOrderDate);
    const spent = formatMoney(summary.totalSpent, null);
    const meta = [
      date ? `Last order ${date}` : null,
      summary.ordersCount !== null ? `${summary.ordersCount} ${summary.ordersCount === 1 ? 'order' : 'orders'}` : null,
    ].filter((v): v is string => typeof v === 'string');
    return (
      <>
        <div className="row" style={{ marginBottom: 0 }}>
          <div className="product-mark">
            <Icon id="box" />
          </div>
          <div>
            <h3 data-testid="order-card-title">{summary.lastOrderName ?? 'Order on file'}</h3>
            {meta.length > 0 ? <p className="muted" data-testid="order-card-meta">{meta.join(' · ')}</p> : null}
          </div>
        </div>
        <div className="order-info">
          <span className="muted" data-testid="order-card-total">
            {spent ? `${spent} total spent` : 'Total spent not on file'}
          </span>
        </div>
        <p className="muted" data-testid="order-card-window-note">
          {view?.sixtyDayWindow
            ? SIXTY_DAY_WINDOW_NOTE
            : summary.note ?? 'Order detail is not available from Shopify for this contact'}
        </p>
      </>
    );
  }
  if (order.status === 'ready') {
    return (
      <div className="row" style={{ marginBottom: 0 }}>
        <div className="product-mark">
          <Icon id="box" />
        </div>
        <p className="muted" data-testid="order-card-none">{NO_ORDERS_TEXT}</p>
      </div>
    );
  }
  // 'not-configured' (503 on this deploy) and 'skipped'.
  return (
    <div className="row" style={{ marginBottom: 0 }}>
      <div className="product-mark">
        <Icon id="box" />
      </div>
      <p className="muted" data-testid="order-card-empty">{ORDER_SYNC_PLACEHOLDER}</p>
    </div>
  );
}

export const VIEW_ORDER_DETAILS_LABEL = 'View order details';

/** Tooltip on the inert "View order details" link: why there is nothing to open. */
export function viewOrderDetailsTitle(status: LatestOrderStatus): string {
  switch (status) {
    case 'loading':
      return 'Loading latest order';
    case 'error':
      return 'Latest order could not load';
    case 'not-configured':
      return 'Order detail opens once the Shopify order sync is linked';
    default:
      return 'No Shopify order on file to open';
  }
}

// W40.1 (M5): the approved card-link "View order details" is always
// present. It opens the History tab (every order, same route the W29
// "View all orders" link used) when an order or order summary is on
// file, and stays visible but inert with an honest tooltip otherwise.
function RealOrderCard({ customerId }: { customerId: string | null }): ReactNode {
  const navigate = useNavigate();
  const order = useLatestOrder(customerId);
  const view = order.status === 'ready' ? order.view : null;
  const hasOrders = Boolean(view && (view.latest || view.summary));
  const canOpen = hasOrders && !!customerId;
  return (
    <section className="card" data-testid="order-card">
      <div className="context" aria-busy={order.status === 'loading'}>
        <span className="eyebrow">Latest order</span>
        <LatestOrderBody order={order} />
      </div>
      <button
        className="card-link"
        type="button"
        data-testid="order-card-link"
        disabled={!canOpen}
        title={canOpen ? undefined : viewOrderDetailsTitle(order.status)}
        onClick={() => {
          if (canOpen && customerId) navigate(`/history/${encodeURIComponent(customerId)}`);
        }}
      >
        {VIEW_ORDER_DETAILS_LABEL}
        <Icon id="arrow" />
      </button>
    </section>
  );
}

function RealRightStack(props: Extract<RightStackProps, { mode: 'real' }>): ReactNode {
  const params = useParams();
  const customerId = props.customerId ?? params.customerId ?? null;
  return (
    <aside className="right-stack" aria-label="Customer context">
      <RealHorseCard horses={props.horses} onViewHorses={props.onViewHorses} />
      <RealOrderCard customerId={customerId} />
    </aside>
  );
}

export function RightStack(props: RightStackProps = {}): ReactNode {
  if (props.mode === 'real') {
    return <RealRightStack {...props} />;
  }
  return (
    <aside className="right-stack" aria-label="Customer context">
      <section className="card">
        <div className="context">
          <span className="eyebrow">Every horse has a story</span>
          <div className="row">
            <img
              className="horse-portrait"
              src={HORSE_PORTRAIT_PLACEHOLDER}
              alt={`Illustrative horse portrait for ${DUKE.name}`}
            />
            <div>
              <h3>{DUKE.name}</h3>
              <p className="muted">{CURRENT_ACTION_MOCK.horseBreedDisciplineAge}</p>
            </div>
          </div>
          <dl>
            <dt>Product</dt>
            <dd>{CURRENT_ACTION_MOCK.horseProduct}</dd>
            <dt>Routine</dt>
            <dd>{CURRENT_ACTION_MOCK.horseRoutine}</dd>
            <dt>Goal</dt>
            <dd>{CURRENT_ACTION_MOCK.horseGoal}</dd>
          </dl>
        </div>
        <button
          className="card-link"
          type="button"
          onClick={() => {
            // TODO(runtime): navigate to Horses tab for this contact.
          }}
        >
          View horse &amp; feeding details
          <Icon id="arrow" />
        </button>
      </section>
      <section className="card">
        <div className="context">
          <span className="eyebrow">Latest order</span>
          <div className="row" style={{ marginBottom: 0 }}>
            <div className="product-mark">
              <Icon id="box" />
            </div>
            <div>
              <h3>Gut X</h3>
              <p className="muted">Order {CURRENT_ACTION_MOCK.orderId} · First order</p>
            </div>
            <span className="badge" style={{ marginLeft: 'auto' }}>
              {CURRENT_ACTION_MOCK.orderStatus}
            </span>
          </div>
          <div className="order-info">
            <span className="muted">{CURRENT_ACTION_MOCK.orderSubscriptionLabel}</span>
            <span style={{ color: '#37735c' }}>{CURRENT_ACTION_MOCK.orderSubscriptionState}</span>
          </div>
        </div>
        <button
          className="card-link"
          type="button"
          onClick={() => {
            // TODO(runtime): open order detail modal / navigate to order view.
          }}
        >
          View order details
          <Icon id="arrow" />
        </button>
      </section>
    </aside>
  );
}
